import { useState, useEffect } from "react";
import { Volume2, Search, ChevronDown, ChevronUp, RotateCcw, Check, X, Plus, Sparkles } from "lucide-react";
import vocabData from "../data/vocabulary.json";
import { earnXP } from "../gamification";

const colorMap = {
  indigo: { bg: "bg-paper-deep", border: "border-ink-100", text: "text-gold-700", badge: "bg-cream-100 text-gold-700" },
  amber: { bg: "bg-gold-50", border: "border-gold-300", text: "text-gold-700", badge: "bg-gold-100 text-gold-700" },
  emerald: { bg: "bg-sage-light", border: "border-sage-light", text: "text-sage-dark", badge: "bg-sage-light text-sage-dark" },
  blue: { bg: "bg-sky-light", border: "border-sky-light", text: "text-sky-dark", badge: "bg-sky-light text-sky-dark" },
  red: { bg: "bg-berry-light", border: "border-berry-light", text: "text-berry", badge: "bg-berry-light text-berry-dark" },
  purple: { bg: "bg-gold-100", border: "border-gold-300", text: "text-gold-700", badge: "bg-gold-100 text-gold-700" },
};
const colors = Object.keys(colorMap);

function getFlashcards() {
  try { return JSON.parse(localStorage.getItem("flashcards") || "[]"); } catch { return []; }
}
function saveFlashcards(cards) { localStorage.setItem("flashcards", JSON.stringify(cards)); }

export default function Vocabulary() {
  const [search, setSearch] = useState("");
  const [openCategory, setOpenCategory] = useState(null);
  const [mode, setMode] = useState("browse"); // browse | flashcard | add
  const [flashcards, setFlashcards] = useState(getFlashcards);
  const [cardIndex, setCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [score, setScore] = useState({ correct: 0, wrong: 0 });
  const [newWord, setNewWord] = useState({ word: "", meaning: "", example: "" });

  // Build flashcard deck from vocab data
  const allWords = [];
  vocabData.forEach((cat) => cat.words.forEach((w) => allWords.push({ ...w, category: cat.category })));

  const dueCards = flashcards.filter((f) => {
    if (!f.nextReview) return true;
    return new Date(f.nextReview) <= new Date();
  });

  function initFlashcardsFromVocab() {
    const existing = getFlashcards();
    const existingWords = new Set(existing.map((f) => f.word.toLowerCase()));
    const newCards = [];
    allWords.forEach((w) => {
      if (!existingWords.has(w.word.toLowerCase())) {
        newCards.push({ word: w.word, meaning: w.meaning, example: w.example, category: w.category, timesReviewed: 0, timesCorrect: 0, nextReview: null, custom: false });
      }
    });
    const merged = [...existing, ...newCards];
    saveFlashcards(merged);
    setFlashcards(merged);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (flashcards.length === 0) initFlashcardsFromVocab(); }, []);

  function startReview() {
    setCardIndex(0); setFlipped(false); setScore({ correct: 0, wrong: 0 });
    setMode("flashcard");
  }

  function markCard(correct) {
    const card = (dueCards.length > 0 ? dueCards : flashcards)[cardIndex];
    if (card) {
      const updated = flashcards.map((f) => {
        if (f.word === card.word) {
          const reviewed = (f.timesReviewed || 0) + 1;
          const correctCount = correct ? (f.timesCorrect || 0) + 1 : (f.timesCorrect || 0);
          // Spaced repetition: correct = longer interval
          const days = correct ? Math.min(reviewed * 2, 30) : 1;
          const next = new Date(); next.setDate(next.getDate() + days);
          return { ...f, timesReviewed: reviewed, timesCorrect: correctCount, nextReview: next.toISOString() };
        }
        return f;
      });
      saveFlashcards(updated);
      setFlashcards(updated);
      if (correct) earnXP(5, "vocabulary");
    }
    setScore((s) => correct ? { ...s, correct: s.correct + 1 } : { ...s, wrong: s.wrong + 1 });
    setFlipped(false);
    const deck = dueCards.length > 0 ? dueCards : flashcards;
    if (cardIndex < Math.min(deck.length - 1, 9)) {
      setCardIndex((i) => i + 1);
    } else {
      setMode("result");
    }
  }

  function addCustomWord() {
    if (!newWord.word.trim() || !newWord.meaning.trim()) return;
    const card = { ...newWord, timesReviewed: 0, timesCorrect: 0, nextReview: null, custom: true, category: "Custom" };
    const updated = [...flashcards, card];
    saveFlashcards(updated);
    setFlashcards(updated);
    setNewWord({ word: "", meaning: "", example: "" });
    earnXP(5, "vocabulary");
  }

  function speak(text) {
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = Number(localStorage.getItem("speechRate") || "0.85");
      u.lang = "en-US";
      window.speechSynthesis.speak(u);
    }
  }

  // Flashcard Review Mode
  if (mode === "flashcard") {
    const deck = dueCards.length > 0 ? dueCards : flashcards;
    const card = deck[cardIndex];
    if (!card) { setMode("browse"); return null; }
    return (
      <div className="space-y-3 page-enter">
        <div className="flex items-center justify-between">
          <button onClick={() => setMode("browse")} className="text-gold-700 hover:text-gold-700 link-animate text-sm">&larr; Back</button>
          <span className="text-sm text-gray-400">{cardIndex + 1} / {Math.min(deck.length, 10)}</span>
        </div>
        <div onClick={() => setFlipped(!flipped)} className="cursor-pointer bg-white rounded-2xl shadow-lg card-hover border border-gray-100 p-5 min-h-[180px] flex flex-col items-center justify-center text-center hover:shadow-xl transition-all">
          {!flipped ? (
            <>
              <p className="text-2xl font-bold text-gray-900 mb-3">{card.word}</p>
              <button onClick={(e) => { e.stopPropagation(); speak(card.word); }} className="text-gold-700 hover:text-gold-700"><Volume2 className="w-5 h-5" /></button>
              <p className="text-sm text-gray-400 mt-4">Tap to reveal meaning</p>
            </>
          ) : (
            <>
              <p className="text-lg text-gray-500 mb-2">{card.word}</p>
              <p className="text-2xl font-bold text-gold-700 mb-3">{card.meaning}</p>
              {card.example && <p className="text-sm text-gray-500 italic">"{card.example}"</p>}
            </>
          )}
        </div>
        {flipped && (
          <div className="flex gap-3 justify-center">
            <button onClick={() => markCard(false)} className="flex items-center gap-2 bg-berry-light text-berry border border-berry-light px-6 py-3 rounded-xl font-semibold hover:bg-berry-light transition-all">
              <X className="w-5 h-5" /> Hard
            </button>
            <button onClick={() => markCard(true)} className="flex items-center gap-2 bg-sage-light text-sage-dark border border-sage-light px-6 py-3 rounded-xl font-semibold hover:bg-sage-light transition-all">
              <Check className="w-5 h-5" /> Got it!
            </button>
          </div>
        )}
      </div>
    );
  }

  // Result Mode
  if (mode === "result") {
    return (
      <div className="space-y-3 page-enter">
        <div className="bg-white rounded-2xl shadow-lg card-hover p-5 text-center">
          <div className="text-3xl mb-4">{score.correct > score.wrong ? "🎉" : "💪"}</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Complete!</h2>
          <div className="flex justify-center gap-4 my-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-sage-dark">{score.correct}</p>
              <p className="text-sm text-gray-500">Correct</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-berry">{score.wrong}</p>
              <p className="text-sm text-gray-500">Needs Review</p>
            </div>
          </div>
          <p className="text-gold-700 font-semibold mb-3">+{score.correct * 5} XP earned</p>
          <div className="flex gap-3 justify-center">
            <button onClick={startReview} className="bg-ink-900 text-white px-6 py-3 rounded-xl font-semibold hover:bg-ink-900 transition-all">
              <RotateCcw className="w-4 h-4 inline mr-2" /> Review Again
            </button>
            <button onClick={() => setMode("browse")} className="border border-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all">
              Browse Words
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Browse Mode
  const filtered = vocabData.map((cat) => ({
    ...cat,
    words: cat.words.filter((w) => !search || w.word.toLowerCase().includes(search.toLowerCase()) || w.meaning.toLowerCase().includes(search.toLowerCase())),
  })).filter((cat) => cat.words.length > 0);

  return (
    <div className="space-y-4 page-enter relative">
      <div className="blob blob-purple" style={{ width: 320, height: 320, top: -40, right: -40, opacity: 0.18 }} />
      <div className="blob blob-cyan" style={{ width: 260, height: 260, top: 200, left: -40, opacity: 0.15 }} />

      <div className="relative flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-normal"><em>Vocabulary.</em></h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 font-semibold mt-1">{flashcards.length} words — {dueCards.length} due for review</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setMode("add")} className="btn-premium bg-white text-gray-700 border-2 border-gray-200 hover:border-gold-500 px-4 py-2 text-sm">
            <Plus className="w-4 h-4" /> Add Word
          </button>
          <button onClick={startReview} className="btn-premium btn-premium-primary px-4 py-2 text-sm">
            <Sparkles className="w-4 h-4" /> Review {dueCards.length > 0 ? `(${dueCards.length})` : ""}
          </button>
        </div>
      </div>

      {/* Add Word Form */}
      {mode === "add" && (
        <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-3">
          <h3 className="font-bold text-gray-900">Add Custom Word</h3>
          <input value={newWord.word} onChange={(e) => setNewWord({ ...newWord, word: e.target.value })} placeholder="English word" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-gold-500 focus:outline-none" />
          <input value={newWord.meaning} onChange={(e) => setNewWord({ ...newWord, meaning: e.target.value })} placeholder="Meaning" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-gold-500 focus:outline-none" />
          <input value={newWord.example} onChange={(e) => setNewWord({ ...newWord, example: e.target.value })} placeholder="Example sentence (optional)" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-gold-500 focus:outline-none" />
          <div className="flex gap-2">
            <button onClick={addCustomWord} className="bg-ink-900 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-ink-900">Add</button>
            <button onClick={() => setMode("browse")} className="text-gray-500 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search words..." className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-gold-500 focus:outline-none" />
      </div>

      {/* Categories */}
      {filtered.map((cat, ci) => {
        const c = colorMap[colors[ci % colors.length]];
        const isOpen = openCategory === cat.category;
        return (
          <div key={cat.category} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <button onClick={() => setOpenCategory(isOpen ? null : cat.category)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${c.badge}`}>{cat.words.length}</span>
                <h3 className="font-semibold text-gray-900">{cat.category}</h3>
              </div>
              {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {isOpen && (
              <div className="border-t border-gray-100 divide-y divide-gray-50">
                {cat.words.map((w, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                    <button onClick={() => speak(w.word)} className="text-gray-300 hover:text-gold-700 transition-colors"><Volume2 className="w-4 h-4" /></button>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{w.word}</p>
                      <p className="text-xs text-gray-500">{w.meaning}</p>
                    </div>
                    {w.example && <p className="text-xs text-gray-400 hidden sm:block max-w-[200px] truncate italic">"{w.example}"</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
